import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Uniform collapse chrome for the right-column context panels on the ticket
 * detail page. All three panels (Application Status, Past Tickets, Response
 * History) render inside this so their headers and toggle affordance stay
 * visually consistent.
 *
 * Height behaviour is controlled by two mutually exclusive props:
 *
 *   - `scrollWhenOpen` — panel grows to claim leftover flex-column space on
 *     the parent aside while open; its body becomes the scroll region. Use
 *     for panels whose content can be long (Past Tickets).
 *
 *   - `bodyMaxHeightClass` — panel keeps a compact fixed cap regardless of
 *     available space. Its body scrolls internally if content exceeds the cap.
 *     Use for panels whose content is short by nature (Response History).
 *
 * When neither is set the panel takes its natural height (Application Status).
 */
export function RightPanelDisclosure({
  title,
  icon,
  trailing,
  defaultOpen = false,
  scrollWhenOpen = false,
  bodyMaxHeightClass,
  openMaxHeightClass,
  containerClassName,
  children,
}: {
  title: string;
  icon?: ReactNode;
  trailing?: ReactNode;
  defaultOpen?: boolean;
  scrollWhenOpen?: boolean;
  bodyMaxHeightClass?: string;
  /**
   * Cap on the outer wrapper when open. Combine with `scrollWhenOpen` to make
   * the panel grow via flex-1 *up to* this cap; content beyond it scrolls.
   * Pass a Tailwind max-height utility, e.g. `"lg:max-h-96"`.
   */
  openMaxHeightClass?: string;
  /** Extra classes for the outer card wrapper — used e.g. to override the
   * default surface bg with a status-driven pastel. */
  containerClassName?: string;
  children: ReactNode;
}) {
  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => {
        const growable = scrollWhenOpen && open;
        const capped = !!bodyMaxHeightClass && open;
        const outerCap = openMaxHeightClass && open ? openMaxHeightClass : "";
        return (
          <div
            className={`card overflow-hidden ${
              growable ? "lg:flex lg:flex-col lg:flex-1 lg:min-h-0" : ""
            } ${outerCap} ${containerClassName ?? ""}`}
          >
            <DisclosureButton className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-black/5 dark:hover:bg-white/5">
              <span className="inline-flex items-center gap-2 text-sm font-semibold">
                {icon}
                {title}
              </span>
              <span className="inline-flex items-center gap-2">
                {trailing}
                <ChevronDown
                  size={16}
                  className={`transition-transform ${open ? "rotate-180" : ""}`}
                  aria-hidden
                />
              </span>
            </DisclosureButton>
            <DisclosurePanel
              className={`border-t border-slate-200 dark:border-surface-border ${
                growable ? "lg:flex-1 lg:min-h-0 lg:overflow-y-auto scroll-themed" : ""
              } ${
                capped ? `${bodyMaxHeightClass} overflow-y-auto scroll-themed` : ""
              }`}
            >
              {children}
            </DisclosurePanel>
          </div>
        );
      }}
    </Disclosure>
  );
}
